import React, { useEffect, useMemo, useRef, useState } from "react";
import ProgressTabs from "./HelperFunctions/ProgressBar";

import Section1 from "./HelperFunctions/Sections/Section1";
import Section2 from "./HelperFunctions/Sections/Section2";
import Section3 from "./HelperFunctions/Sections/Section3";
import Section4 from "./HelperFunctions/Sections/Section4";
import Section5 from "./HelperFunctions/Sections/Section5";
import Section6 from "./HelperFunctions/Sections/Section6";
import Section7 from "./HelperFunctions/Sections/Section7";
import Section8 from "./HelperFunctions/Sections/Section8";
import Section9 from "./HelperFunctions/Sections/Section9";
import Section10 from "./HelperFunctions/Sections/Section10";
import Section11 from "./HelperFunctions/Sections/Section11";

import errorTrackerV2 from "./HelperFunctions/Validation/Utils/ErrorTrackerV2";

import "../../../css/Recruitment/RecruitmentForm.css";

import { writeDataS12 } from "../../../backend/Recruitment/Sections/Section12/writeDataS12";
import { readDataS12 } from "../../../backend/Recruitment/Sections/Section12/readDataS12";
import { getApplicationStatus } from "../../../backend/Recruitment/Sections/RecruitmentForm";

// Import save functions for auto-save on navigation
import { saveSectionBeforeNavigation } from "./Utils/saveSectionBeforeNavigation";

import { useReactToPrint } from "react-to-print";
import {
  addSubmissionListener,
  getIsSubmitted,
} from "./Utils/submissionListener";

import Sidebar from "../../DesignSystem/Sidebar";
import SecondaryButton from "../../DesignSystem/Buttons/SecondaryButton";
import DragAndDrop from "./HelperFunctions/DragAndDrop";
import { auth } from "../../../backend/firebaseConfig";

//  new
import {
  validateBeforeNavigate,
  validateAllSections,
} from "../Applications/HelperFunctions/Validation/validateBeforeNavigate";
import { ReactComponent as BackIcon } from "../../../assets/icons/Back.svg";
import { useNavigate } from "react-router-dom";


const TOTAL_STEPS = 11;

const RecruitmentForm = () => {
  const printableRef = useRef(null);

  const [currentStep, setCurrentStep] = useState(1);
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(getIsSubmitted());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [validationStatus, setValidationStatus] = useState(() => {
    const initial = {};
    for (let i = 1; i <= 11; i++) {
      const sectionKey = `section${i}`;
      const sectionState = errorTrackerV2.getSectionState(sectionKey);
      initial[sectionKey] = !sectionState.hasErrors;
    }
    return initial;
  });

  const isFormValid = useMemo(
    () => Object.values(validationStatus).every(Boolean),
    [validationStatus],
  );

  //  mount once — correct dependency
  useEffect(() => {
    const remove = addSubmissionListener(setIsSubmitted);
    setIsSubmitted(getIsSubmitted());
    return () => remove();
  }, []);

  // Init errors + fetch status
  useEffect(() => {
    // Validate all sections on app load (parallel for performance)
    (async () => {
      await validateAllSections();
      const data = await getApplicationStatus();
      setApplicationStatus(data);
    })();
  }, []);

  const fetchData = async () => {
    const data = await getApplicationStatus();
    setApplicationStatus(data);
  };

  // Sync validationStatus from per-section tracker changes
  useEffect(() => {
    const unsubscribers = [];

    // Subscribe to each section independently
    for (let i = 1; i <= 11; i++) {
      const sectionKey = `section${i}`;

      const handleSectionChange = (sectionState) => {
        setValidationStatus((prev) => ({
          ...prev,
          [sectionKey]: !sectionState.hasErrors,
        }));
      };

      const unsubscribe = errorTrackerV2.addSectionListener(
        sectionKey,
        handleSectionChange,
      );

      unsubscribers.push(unsubscribe);
    }

    return () => {
      unsubscribers.forEach((unsubscribe) => {
        if (typeof unsubscribe === "function") {
          unsubscribe();
        }
      });
    };
  }, []);

  // Scroll behaviour
  useEffect(() => {
    const el = printableRef.current;
    if (!el) return;
    el.scrollTop = 0;
  }, [currentStep]);

  const handleValidationStatusChange = (sectionKey, isValid) => {
    setValidationStatus((prev) => ({ ...prev, [sectionKey]: isValid }));
  };

  const sectionProps = (key) => ({
    onValidationStatusChange: (isValid) =>
      handleValidationStatusChange(key, isValid),
  });

  //  key change: save current section, then validate BEFORE navigating
  const handleStepChange = async (nextStep) => {
    // Save current section before navigating away
    if (currentStep !== nextStep) {
      await saveSectionBeforeNavigation(currentStep);
    }

    await validateBeforeNavigate(nextStep);
    setCurrentStep(nextStep);
  };

  const goToNextStep = async () => {
    const next = Math.min(TOTAL_STEPS, currentStep + 1);
    await handleStepChange(next);
  };

  const goToPreviousStep = () => {
    setCurrentStep((s) => Math.max(1, s - 1));
  };

  const handlePrint = useReactToPrint({
    content: () => printableRef.current,
  });

  const sendEmailConfirmation = async () => {
    try {
      const response = await fetch(
        "https://rosemount-explore.onrender.com/SendRecruitmentApplicationConfirmation",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            adviserName: auth.currentUser?.displayName || "",
            date: new Date().toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "2-digit",
            }),
          }),
        },
      );

      await response.json().catch(() => null);
    } catch (_) {}
  };

  const handleSubmit = async () => {
    // Force a final validation pass before submit:
    await validateBeforeNavigate(11);

    if (!isFormValid) {
      alert("Form is invalid. Please correct errors in the sections.");
      return;
    }

    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      await writeDataS12();
      await sendEmailConfirmation();
      handlePrint?.();
      await fetchData();
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    readDataS12().then((data) => {
      if (data) fetchData();
    });
  }, []);

  const renderSection = () => {
    switch (currentStep) {
      case 1:
        return <Section1 {...sectionProps("section1")} />;
      case 2:
        return <Section2 {...sectionProps("section2")} />;
      case 3:
        return <Section3 {...sectionProps("section3")} />;
      case 4:
        return <Section4 {...sectionProps("section4")} />;
      case 5:
        return <Section5 {...sectionProps("section5")} />;
      case 6:
        return <Section6 {...sectionProps("section6")} />;
      case 7:
        return <Section7 {...sectionProps("section7")} />;
      case 8:
        return <Section8 {...sectionProps("section8")} />;
      case 9:
        return <Section9 {...sectionProps("section9")} />;
      case 10:
        return <Section10 {...sectionProps("section10")} />;
      case 11:
        return <Section11 {...sectionProps("section11")} />;
      default:
        return <Section1 {...sectionProps("section1")} />;
    }
  };

  const isSubmitDisabled = !isFormValid || isSubmitting;

  useEffect(() => {
    // Global listener for comprehensive error logging/diagnostics
    const unsubscribe = errorTrackerV2.addGlobalListener?.((allStates) => {
      // Build detailed error report
      const snapshot = {};
      const detailedErrors = {};
      let totalErrorCount = 0;

      Object.keys(allStates || {}).forEach((k) => {
        const sectionState = allStates[k];
        snapshot[k] = {
          hasErrors: sectionState.hasErrors,
          errorCount: sectionState.errorCount,
        };
        totalErrorCount += sectionState.errorCount;

        // Capture detailed error messages
        if (
          sectionState.errors &&
          Object.keys(sectionState.errors).length > 0
        ) {
          detailedErrors[k] = sectionState.errors;
        }
      });

      // console.clear();
      // console.log(
      //   "════════════════════════════════════════════════════════════",
      // );
      // console.log(
      //   "           RECRUITMENT FORM - ERROR LOG REPORT              ",
      // );
      // console.log(
      //   "════════════════════════════════════════════════════════════",
      // );
      // console.log(`\nSUMMARY: Total Errors: ${totalErrorCount}`);
      // console.table(
      //   Object.keys(snapshot).map((k) => ({
      //     section: k.toUpperCase(),
      //     hasErrors: snapshot[k].hasErrors ? "YES" : "NO",
      //     errorCount: snapshot[k].errorCount,
      //   })),
      // );

      // Detailed errors per section
      // if (Object.keys(detailedErrors).length > 0) {
      //   console.log("\nDETAILED ERRORS BY SECTION:\n");
      //   Object.entries(detailedErrors).forEach(([section, errors]) => {
      //     console.group(
      //       `\n${section.toUpperCase()} (${Object.keys(errors).length} errors)`,
      //     );
      //     Object.entries(errors).forEach(([fieldName, errorMsg]) => {
      //       console.log(`  ${fieldName}: ${errorMsg}`);
      //     });
      //     console.groupEnd();
      //   });
      // } else {
      //   console.log("\nNO DETAILED ERRORS TO REPORT\n");
      // }

      // Full state dump for debugging
      // console.log("\nFULL STATE DUMP:");
      // console.log(allStates);
    });

    return () => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, []);

  // return applicationStatus ? (
  //   <div className="main-content">
  //     <Sidebar />
  //     <div className="onSubmit">
  //       <DragAndDrop />
  //       <h2>Application submitted!</h2>
  //     </div>
  //   </div>
  // ) :
  const navigate = useNavigate();
  return (
    <div className="main-content">
      <Sidebar />

      <div className="recruitment-form-container">
        <BackIcon
          className="backIcon"
          onClick={() => {
            navigate(-1);
          }}
        />
        <div className="recruitment-layout">
          {/*  pass onStepChange, NOT setCurrentStep */}
          <ProgressTabs
            currentStep={currentStep}
            onStepChange={handleStepChange}
          />

          <div className="recruitment-right">
            <div className="section-scroll" ref={printableRef}>
              {renderSection()}
            </div>

            <div className="navigation-buttons">
              {/* <SecondaryButton
                label="Previous Step"
                onClick={goToPreviousStep}
                disabled={currentStep === 1}
              /> */}

              {currentStep === 11 ? (
                <SecondaryButton
                  label="Submit Application"
                  onClick={handleSubmit}
                  disabled={isSubmitDisabled}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecruitmentForm;
